import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { FileInfo } from '@progress/kendo-angular-upload';
import { HerkunftsnachweisDialogComponent, HerkunftsnachweisDialogResult } from '../herkunftsnachweis-dialog/herkunftsnachweis-dialog.component';
import { DocumentStatus } from '@/core/enums/document-status.enum';
import { AnfrageService, DocumentService } from '@/api/service-index';
import { PortalkonfigurationDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/portalkonfiguration-display-model';
import { DocumenttypeDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/documenttype-display-model';
import { DocumentDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/document-display-model';
import { FileTooLargeNotification } from '@/core/utils/notifications/document/file-too-large.notification';
import { InvalidMimeTypeNotification } from '@/core/utils/notifications/document/invalid-mime-type.notification';
import { StateService } from '@/core/services/state.service';
import { FileAlreadyExistsNotification } from '@/core/utils/notifications/document/file-already-exists.notification';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { I18nService } from '@/core/services/i18nService';
import { ConfirmationDialogAction } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { GlobalEnumsAceDokumentenart } from '@/api/model-index';
import { DocumentService as ClientDocumentService } from '@/core/services/document.service';
import { Constants } from '@/constants';
import { GutachtenService } from '../../../../api/services/gutachten.service';

@Component({
  selector: 'document-view',
  templateUrl: './document-view.component.html',
  styleUrls: ['./document-view.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocumentViewComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DocumentViewComponent),
      multi: true
    }
  ]
})
export class DocumentViewComponent implements ControlValueAccessor, OnInit {
  protected onChange: any = () => { };
  protected onTouched: any = () => { };
  public originalDocuments: DocumentDisplayModel[] = [];
  public documents: DocumentDisplayModel[] = [];
  protected _documentType: DocumenttypeDisplayModel | undefined;
  protected herkunftsnachweisDialog: DialogRef;
  protected _disabledState: boolean;
  protected portalkonfiguration: PortalkonfigurationDisplayModel;
  protected _documentStatus: DocumentStatus;

  @Output('documentDelete') documentDeleteEventEmitter: EventEmitter<number> = new EventEmitter<number>();
  @Output('documentUpload') documentUploadEventEmitter: EventEmitter<DocumentDisplayModel> = new EventEmitter<DocumentDisplayModel>();

  public get documentType(): DocumenttypeDisplayModel | undefined {
    return this._documentType;
  }

  public get documentStatus(): DocumentStatus {
    return this._documentStatus;
  }

  public get showHerkunftsnachweisHeader(): boolean {
    var herkunftsnachweisFound = this.filteredDocuments.find(element => element.herkunftsnachweis != null && element.herkunftsnachweis != "keine Angabe");
    return herkunftsnachweisFound != null;
  }

  @Input()
  public set documentType(documentType: DocumenttypeDisplayModel | undefined) {
    this._documentType = documentType;
  }

  @Input()
  public paymentId: string;

  public get isNoEntryMessageVisible(): boolean {
    return !this.filteredDocuments || this.filteredDocuments.length < 1;
  }

  public get isTemplateDownloadButtonVisible(): boolean {
    return this._documentType?.hasVorlage != null && this._documentType.hasVorlage;
  }

  public get value(): DocumentDisplayModel[] {
    return this.documents;
  }

  public set value(value: DocumentDisplayModel[]) {
    this.originalDocuments = value;

    this.documents = this.copyArray<DocumentDisplayModel>(value);

    this.onChange(value);
    this.onTouched();
  }

  public get filteredDocuments(): DocumentDisplayModel[] {
    return this.documents?.filter(entry => (entry.documententyp && entry.documententyp.documenttypeId == this.documentType?.documenttypeId)) ?? [];
  }

  public get acceptedFileFormats(): string {
    if (this.documentType) {
      let accepted: string = "";
      this.documentType?.erlaubteDateiendungen?.forEach(ext => {
        accepted += "." + ext + ", "
      });
      return accepted.substr(0, accepted.lastIndexOf(", "));
    }
    return "*.*";
  }

  public get isOpenDocumentButtonDisabled(): boolean {
    return (this.reachedMaxOneDocument) || (this.reachedMaxNumberOfDocuments) || this.isDisabled;
  }

  public get reachedMaxOneDocument(): boolean {
    return !this.documentType?.allowMultiUpload && this.filteredDocuments.length >= 1;
  }

  public get reachedMaxNumberOfDocuments(): boolean {
    return !!this.documentType?.maxAnzahlDerAnlagen && this.documentType?.maxAnzahlDerAnlagen <= this.filteredDocuments.length;
  }

  public get isDisabled(): boolean {
    return this._disabledState;
  }

  constructor(protected stateService: StateService,
    protected portalContextService: PortalContextService,
    protected loadingAnimationService: LoadingAnimationService,
    protected anfrageService: AnfrageService,
    protected route: ActivatedRoute,
    protected dialogService: DialogService,
    protected clientDocumentService: ClientDocumentService,
    protected documentService: DocumentService,
    protected languageservice: I18nService,
    protected gutachtenService: GutachtenService) {
    //
  }

  ngOnInit(): void {
    this.portalkonfiguration = this.route.snapshot.data.portalkonfiguration;
  }

  public onValueChange(event: FileInfo[]) {
    this.onChange(this.value);
    this.onTouched();
  }

  public onFileUploadChange(event: any) {
    this._openDocument(event);
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.documents = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(_: UntypedFormControl) {
    return this.documentType && ((this.documentType?.isUploadRequired && this.filteredDocuments.length < 1) || (!this.documentType?.allowMultiUpload && this.filteredDocuments.length > 1)) ? { address: { valid: false } } : null;
  }

  setDisabledState(isDisabled: boolean) {
    this._disabledState = isDisabled;
  }

  public onTemplateDownloadDocumentClick() {
    this.downloadTemplate();
  }

  public downloadTemplate() {
    if (this._documentType?.hasVorlage) {
      if (this.portalContextService.CurrentAnfrage) {
        let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentVorlageDocumententypIdGet(
          { anfrageId: this.portalContextService.CurrentAnfrage.id, documententypId: this._documentType?.documenttypeId!, reportId: this.portalContextService.CurrentBericht?.projektberichtId, gutachtenId: this.portalContextService.CurrentGutachten?.gutachtenId ?? undefined })
          .subscribe(file => {
            this.clientDocumentService.download(file)
          });

        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
      }
    }
  }

  public onDocumentDelete(document: DocumentDisplayModel) {
    this.deleteDocument(document);
  }

  protected uploadDocument(document: DocumentDisplayModel) {
    this._documentStatus = DocumentStatus.New;

    var dokumententyp = this.documents[this.documents.indexOf(document)].documententyp;

    if (this.portalContextService.CurrentAnfrage) {
      const id: string = this.portalContextService.CurrentAnfrage.id;
      const reportId: string | undefined = this.portalContextService.CurrentBericht?.projektberichtId;
      const gutachtenId: string | null | undefined = this.portalContextService.CurrentGutachten?.gutachtenId;

      let request: any = { anfrageId: id, body: document, projektberichtId: reportId, gutachtenId: gutachtenId };
      if (this.paymentId)
        request.paymentId = this.paymentId;

      if (this.documentType && this.documentType.dokumentenart === GlobalEnumsAceDokumentenart.Verwendungsnachweis) {
        let subscription = this.documentService
          .apiDocumentAnfrageIdUploadVerwendungsnachweisPost(request)
          .subscribe(response => this.attachmentSubscriptionHandler(response, dokumententyp, document, id),
            error => {
              this.documents.splice(this.documents.indexOf(document), 1);
              throw error;
            });

        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);

      }
      else {
        let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentPatch(request)
          .subscribe(response => this.attachmentSubscriptionHandler(response, dokumententyp, document, id),
            error => {
              this.documents.splice(this.documents.indexOf(document), 1);
              throw error;
            });
        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
      }
    }
  }

  attachmentSubscriptionHandler(response: DocumentDisplayModel,
    dokumententyp: DocumenttypeDisplayModel | undefined,
    document: DocumentDisplayModel, id: string) {
    this._documentStatus = DocumentStatus.Uploaded;

    if (response) {
      response.documententyp = dokumententyp;
      this.documents[this.documents.indexOf(document)] = response;

      this.portalContextService.CurrentAnfrage.attachments?.push(response);
      this.documentUploadEventEmitter.emit(response);
    }

    this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: id, reportId: this.portalContextService.CurrentBericht?.projektberichtId, gutachtenId: this.portalContextService.CurrentGutachten?.gutachtenId ?? undefined }).subscribe(result => {
      this.stateService.ValidationErrors = result;
    });
  }

  protected deleteDocument(document: DocumentDisplayModel) {
    var index = this.originalDocuments.findIndex(element => element.name == document.name);

    this.documents = this.documents.filter(documentItem => documentItem.name != document.name);
    this.originalDocuments = this.originalDocuments.filter(documentItem => documentItem.name != document.name);
    this.portalContextService.CurrentAnfrage.attachments = this.portalContextService.CurrentAnfrage.attachments?.filter(attachment => attachment.name != document.name);

    this.documentDeleteEventEmitter.emit(index);
  }

  protected _openDocument(event: any) {
    const reader = new FileReader();
    let fileToUpload: File = event.target.files[0];
    let fileSizeInMB = fileToUpload.size / 1024 / 1024;

    if (this.portalkonfiguration?.maxFileSizeMB && fileSizeInMB > this.portalkonfiguration.maxFileSizeMB) {
      throw new FileTooLargeNotification(`File is too large. Max allowed filesize: ${this.portalkonfiguration.maxFileSizeMB} MB. Current File size ${fileSizeInMB} MB`, this.languageservice.instant("filetoolarge_notificationtext", { maxfilesize: this.portalkonfiguration.maxFileSizeMB }));
    }

    let existingDoc = this.originalDocuments?.filter(documentItem => documentItem.name == fileToUpload.name);
    if (existingDoc?.length > 0) {
      throw new FileAlreadyExistsNotification(`File with the name '${fileToUpload.name}' already exists on the current project.`, this.languageservice.instant("filealreadyexists_notificationtext", { filename: fileToUpload.name }));
    }

    let document: DocumentDisplayModel = new DocumentDisplayModel();

    document.name = event.target.files[0].name;
    document.contentMimeType = event.target.files[0].type;

    if (this.documentType)
      document.documententyp = this.documentType;

    if (this.documentType && !this.documentType?.erlaubteDateiendungen?.includes(this.documentFileExtension(document))) {
      throw new InvalidMimeTypeNotification("Invalid mimetype for document");
    }

    reader.onload = (e: any) => {
      if (!e.target.result)
        return;

      let base64String: string = (<string>e.target.result).split(",")[1];

      document.content = base64String;

      this.documents.push(document);
      this.uploadDocument(document);
      return;
    };

    if (this.documentType?.herkunftsnachweis) {
      let dialog: DialogRef = this.openHerkunftsnachweisDialog();

      dialog.result.subscribe(result => {
        let herkunftsnachweisResult: HerkunftsnachweisDialogResult = <HerkunftsnachweisDialogResult>result;

        if (herkunftsnachweisResult.action == ConfirmationDialogAction.Confirm) {
          document.herkunftsnachweis = herkunftsnachweisResult.herkunftsnachweis;
          reader.readAsDataURL(fileToUpload);
        }
      })
    } else {
      reader.readAsDataURL(fileToUpload);
    }
  }

  public documentFileExtension(document: DocumentDisplayModel): string {
    if (document.name && document.name.indexOf(".") != -1) {
      return document.name.substr(document.name.lastIndexOf(".") + 1);
    }
    return "";
  }

  protected copyArray<TArray>(array: Array<TArray>): Array<TArray> {
    let copiedArray: Array<TArray> = [];

    array.forEach(entry => copiedArray.push(entry));

    return copiedArray;
  }

  protected openHerkunftsnachweisDialog(): DialogRef {
    this.herkunftsnachweisDialog = this.dialogService.open({
      content: HerkunftsnachweisDialogComponent,
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      width: Constants.DIALOG.WIDTH,
      title: this.languageservice.instant("copyright"),
    });

    let dialogComponent: HerkunftsnachweisDialogComponent = this.herkunftsnachweisDialog.content.instance;

    return this.herkunftsnachweisDialog;
  }
}
